import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import { Warning, ArrowsClockwise, FileText, CheckCircle } from '@phosphor-icons/react';
import api from '../../services/api';
import { formatDate } from '../../lib/utils';

export const EdiErrorsTab = ({ ediLoading, ediErrorSummary, ediErrors, onRefresh }) => {
  const [parseModalOpen, setParseModalOpen] = useState(false);
  const [parseType, setParseType] = useState('999');
  const [ediContent, setEdiContent] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);

  const handleParseEdi = async () => {
    if (!ediContent.trim()) { toast.error('Please paste EDI content'); return; }
    setParsing(true);
    try {
      const endpoint = parseType === '999' ? '/edi-errors/process/999' : '/edi-errors/process/277ca';
      const res = await api.post(endpoint, { edi_content: ediContent });
      setParseResult(res.data);
      toast.success(`Parsed ${parseType}: ${res.data.errors_logged || 0} errors logged`);
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to parse EDI content');
    } finally {
      setParsing(false);
    }
  };

  const handleResolveError = async (errorId) => {
    try {
      await api.post('/edi-errors/resolve', { error_id: errorId, resolution: 'Manually resolved' });
      toast.success('Error marked as resolved');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  if (ediLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-slate-900">{ediErrorSummary?.total || 0}</div>
            <div className="text-sm text-slate-500">Total Errors</div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-700">{ediErrorSummary?.unresolved || 0}</div>
            <div className="text-sm text-red-600">Unresolved</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-emerald-700">{ediErrorSummary?.resolved || 0}</div>
            <div className="text-sm text-emerald-600">Resolved</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6 flex items-center justify-center">
            <Button onClick={() => setParseModalOpen(true)} className="bg-indigo-900 hover:bg-indigo-800" data-testid="parse-edi-btn">
              <FileText size={16} className="mr-2" />
              Parse EDI File
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Errors Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Recent EDI Errors</CardTitle>
        </CardHeader>
        <CardContent>
          {ediErrors.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Warning size={48} className="mx-auto mb-3" />
              <p>No EDI errors found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Claim</TableHead>
                  <TableHead>Error Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ediErrors.map((err) => (
                  <TableRow key={err.id}>
                    <TableCell className="text-xs">{formatDate(err.created_at)}</TableCell>
                    <TableCell><Badge variant="outline">{err.error_type || '999'}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{err.claim_number || '—'}</TableCell>
                    <TableCell className="font-mono text-red-600">{err.error_code}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{err.description}</TableCell>
                    <TableCell>
                      <Badge className={err.resolved ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {err.resolved ? 'Resolved' : 'Open'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {!err.resolved && (
                        <Button variant="ghost" size="sm" onClick={() => handleResolveError(err.id)} data-testid={`resolve-err-${err.id}`}>
                          <CheckCircle size={14} className="mr-1" /> Resolve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Parse EDI Modal */}
      <Dialog open={parseModalOpen} onOpenChange={setParseModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Parse EDI Error File</DialogTitle>
            <DialogDescription>Paste 999 or 277CA EDI content to parse and log errors</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4">
              <Button variant={parseType === '999' ? 'default' : 'outline'} onClick={() => setParseType('999')} className={parseType === '999' ? 'bg-indigo-900' : ''}>
                999 Acknowledgment
              </Button>
              <Button variant={parseType === '277ca' ? 'default' : 'outline'} onClick={() => setParseType('277ca')} className={parseType === '277ca' ? 'bg-indigo-900' : ''}>
                277CA Claim Status
              </Button>
            </div>
            <div className="space-y-2">
              <Label>EDI Content</Label>
              <Textarea value={ediContent} onChange={(e) => setEdiContent(e.target.value)} placeholder={`Paste ${parseType.toUpperCase()} EDI content here...`} className="h-48 font-mono text-xs" data-testid="edi-content-input" />
            </div>
            {parseResult && (
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h4 className="font-semibold mb-2">Parse Result</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Total Transactions: {parseResult.parsed?.summary?.total_transactions || parseResult.parsed?.summary?.total_claims || 0}</div>
                  <div>Errors Logged: {parseResult.errors_logged || 0}</div>
                  <div className="text-emerald-600">Accepted: {parseResult.parsed?.summary?.accepted || 0}</div>
                  <div className="text-red-600">Rejected: {parseResult.parsed?.summary?.rejected || 0}</div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setParseModalOpen(false)}>Close</Button>
            <Button onClick={handleParseEdi} disabled={parsing || !ediContent.trim()} className="bg-indigo-900 hover:bg-indigo-800" data-testid="parse-edi-submit">
              {parsing ? <ArrowsClockwise size={18} className="mr-2 animate-spin" /> : <FileText size={18} className="mr-2" />}
              Parse & Log Errors
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
